import TabSelector from "components/Shared/Filters/TabSelector";
import {
  ENTITY_STATUS_TYPES,
  FILTER_KEYS,
  FUTURE_PRACTICE,
  GAME,
  LIVE_PRACTICE,
  LOAD_EVENTS_TIME_LIVE_PAGE,
  MESSAGES,
  SCOPE,
  STATUS_FILTER,
} from "config/constants";
import React, { useState, useEffect, Fragment } from "react";
import * as DateFns from "date-fns";
import moment from "moment-timezone";
import {
  useDataEventsState,
  useFiltersState,
  useLoadingState,
  usePlayerParticipationDispatch,
  usePlayerParticipationState,
} from "store/hooks";
import SimpleMultiSelector from "components/Shared/Filters/SimpleMultiSelector";
import Table, { HeadsType, RowType } from "../../Shared/CustomTable";
import { useInterval, useRouter } from "helpers/customHooks";
import { PerformanceRoutes } from "../TabPerformance/types";
import PositionFilter from "components/Shared/Filters/PositionFilter";
import Loader from "components/Shared/Loader";
import { PlayerParticipationType } from "store/types";

const PracticeTracking = () => {
  const { dataEvent: data, meta } = useDataEventsState();
  const [show, setShow] = useState(-1);
  const [showFilter, setShowFilter] = useState(-1);
  const close = () => setShowFilter(-1);
  const { loadPlayerParticipation } = usePlayerParticipationDispatch();
  const { playerParticipationData } = usePlayerParticipationState();
  const current = useLoadingState(
    SCOPE.VIEW.PRACTICE.TRACKING.PAGE + ":" + data.meta.gameId
  );
  const [statusObject, setStatusObject] = useState<
    PlayerParticipationType[] | null
  >(null);
  const [playersTracking, setPlayersTracking] = useState(0);
  const [actDevTracking, setActDevTracking] = useState(0);
  const [actDevPlayers, setActDevPlayers] = useState(0);
  const { specificScope: scopeForFilter } = useRouter<PerformanceRoutes>();
  const {
    [FILTER_KEYS.STATUS_TRACKING]: playerStatus,
    [FILTER_KEYS.POSITIONS]: selectedPosition,
  } = useFiltersState(
    scopeForFilter,
    FILTER_KEYS.STATUS_TRACKING,
    FILTER_KEYS.POSITIONS
  );

  useEffect(() => {
    loadPlayerParticipation(data.meta.gameId, true);
  }, [data.meta.gameId]);

  useEffect(() => {
    if (playerParticipationData && playerParticipationData[data.meta.gameId]) {
      setStatusObject(playerParticipationData[data.meta.gameId]);
      let countPlayersTracking = 0;
      let countActDevTracking = 0;
      let countActDevPlayers = 0;
      playerParticipationData[data.meta.gameId].forEach((player) => {
        if (player.tracking) {
          if (
            player.status === ENTITY_STATUS_TYPES.ACT ||
            player.status === ENTITY_STATUS_TYPES.DEV
          ) {
            countActDevTracking++;
          } else {
            countPlayersTracking++;
          }
        }
        if (
          player.status === ENTITY_STATUS_TYPES.ACT ||
          player.status === ENTITY_STATUS_TYPES.DEV
        ) {
          countActDevPlayers++;
        }
      });
      setPlayersTracking(countPlayersTracking);
      setActDevTracking(countActDevTracking);
      setActDevPlayers(countActDevPlayers);
      return () => {
        setPlayersTracking(0);
        setActDevTracking(0);
        setActDevPlayers(0);
      };
    } else {
      setStatusObject(null);
    }
  }, [playerParticipationData, data.meta.gameId]);

  useInterval(
    () => {
      loadPlayerParticipation(data.meta.gameId, false);
    },
    data.meta.type === LIVE_PRACTICE ? LOAD_EVENTS_TIME_LIVE_PAGE : null
  );

  const timeUpdated =
    meta && meta.type === LIVE_PRACTICE
      ? meta.dateUpdated
      : data && data.data && data.data.highlights.updateTimestamp;
  const headers: HeadsType<string, JSX.Element> = [
    { content: "NFL ID", style: { width: "120px" }, class: 't-ll', isOrderly: true },
    { content: "NAME", style: { width: "190px" }, class: 't-ll', isOrderly: true },
    { content: "#", style: { width: "90px" }, class: 't-ll', isOrderly: true },
    { content: "POS.", style: { width: "90px" }, class: 't-ll', isOrderly: true },
    { content: "POS. GROUP", style: { width: "110px" }, class: 't-ll', isOrderly: true },
    { content: "STATUS", style: { width: "90px" }, class: 't-ll', isOrderly: true },
    { content: "TRACKING", style: { width: "585px", textAlign:'left' }, isOrderly: true },
  ];

  let rows: RowType[] = [];

  let filteredStatusPlayers: PlayerParticipationType[] = [];
  let filteredPlayers: PlayerParticipationType[] = [];
  if (
    statusObject &&
    statusObject.length &&
    playerStatus &&
    playerStatus.length > 0
  ) {
    const targetFilter = playerStatus.map((obj) => obj.value);
    filteredStatusPlayers = statusObject.filter(
      (player: PlayerParticipationType) => targetFilter.includes(player.status)
    );
  } else if (statusObject) {
    filteredStatusPlayers = statusObject;
  }

  if (
    selectedPosition &&
    selectedPosition.length > 0 &&
    filteredStatusPlayers
  ) {
    const positionFilter = selectedPosition.map((obj) => obj.value);
    filteredPlayers = filteredStatusPlayers.filter(
      (player: PlayerParticipationType) =>
        positionFilter.includes(player.position)
    );
  } else {
    filteredPlayers = filteredStatusPlayers;
  }

  //@ts-ignore
  rows = filteredPlayers.map((status, index) => {
    return {
      columns: [
        {
          content: status.nflId,
          class: "t-ll tt-qq",
          style:{textAlign: 'center'},
          print: (content: string) => <label>{content}</label>,
        },
        {
          content: status.name,
          class: "t-ll tt-qq",
          style:{textAlign: 'center'},
          print: (content: string) => <label>{content}</label>,
          order: (contentA, contentB) => {
            if (contentA.split(' ').length < 2) {
              return contentA.localeCompare(contentB);
            }
            const lastNameA = contentA.split(' ')[1];
            const lastNameB = contentB.split(' ')[1];
            return lastNameA.localeCompare(lastNameB);
          },
        },
        {
          content: status.jerseyNumber,
          class: "t-ll tt-qq",
          style:{textAlign: 'center'},
          print: (content: string) => <label>{content}</label>,
        },
        {
          content: status.position,
          class: "t-ll tt-qq",
          style:{textAlign: 'center'},
          print: (content: string) => <label>{content}</label>,
        },
        {
          content: status.positionGroup,
          class: "t-ll tt-qq",
          style:{textAlign: 'center'},
          print: (content: string) => <label>{content}</label>,
        },
        {
          content: status.status,
          class: "t-ll tt-qq",
          style:{textAlign: 'center'},
          print: (content: string) => <label>{content}</label>,
        },
        {
          content:{tracking: status.tracking ? 1 : 0, sort: status.jerseyNumber},
          print: (content: string) => (
            <>
              {status.tracking ? (
                <i
                  style={{ fontSize: "25px", color: "green" }}
                  className="mdi mdi-arrow-up-drop-circle"
                />
              ) : (
                <i
                  style={{ fontSize: "25px", color: "red" }}
                  className="mdi mdi-arrow-down-drop-circle"
                />
              )}
            </>
          ),
          order: (contentA, contentB) => {
            if (contentA.tracking !== contentB.tracking) {
              return contentA.tracking - contentB.tracking; 
            }
            return  contentB.sort - contentA.sort;
          },
        },
      ],
      id: index,
    };
  });

  return (
    <>
      <div className="new-manage">
        <Fragment>
          <TabSelector
            show={show === 1}
            open={() => setShow(1)}
            close={() => setShow(-1)}
          />

          <div className="condensed-t condensed-t-status" style={{ marginLeft: "177px" }}>
            <SimpleMultiSelector
              type={STATUS_FILTER}
              show={showFilter === 2}
              close={close}
              open={() => setShowFilter(2)}
              forceScope={scopeForFilter}
            />
          </div>
          <div className="condensed-t condensed-t-position">
            <PositionFilter
              
              show={showFilter === 1}
              close={close}
              open={() => setShowFilter(1)}
              forceScope={scopeForFilter}
            />
          </div>
          <div style={{display: 'flex', justifyContent:'flex-end', width:'calc(100% - 610px)'}} >
          <div className="cont-e">
            <b>ACT/DEV PLAYERS:</b> {actDevPlayers}{" "}
          </div>
          <div className="cont-e">
            <b>ACT/DEV PLAYERS TRACKING:</b> {actDevTracking}{" "}
          </div>
          <div className="cont-e">
            <b>OTHER STATUS PLAYERS TRACKING:</b> {playersTracking}{" "}
          </div>
          </div>
          <div className="p-left" style={{ right: "18px" }}>
            {data.meta.type !== FUTURE_PRACTICE && data.meta.type !== GAME && (
              <label className="l-date">
                Data last updated:{" "}
                {timeUpdated
                  ? DateFns.format(
                      new Date(timeUpdated),
                      "MM/dd/yyyy hh:mm:ssaaaaa"
                    ) + "m "
                  : ""}
                {timeUpdated ? moment.tz(moment.tz.guess()).format("z") : ""}
              </label>
            )}
          </div>
        </Fragment>
      </div>
      <div className="manage-body monitor" style={{ overflow: "hidden" }}>
        <div style={{ height: "100%" }} className="periods-table-box periods-table-box-width">
          <Loader
            forceScope={
              SCOPE.VIEW.PRACTICE.TRACKING.PAGE + ":" + data.meta.gameId
            }
            forceLoader={current.count > 0}
            forceMessage={rows.length === 0 ? MESSAGES.EMPTY : ""}
          >
            <div style={{ height: "100%" }}>
              {/* @ts-ignore */}
              <Table<HeadsType, RowType>
                classTable="default-table-width condensed-view"
                forceScope={"TRACKING"}
                headers={headers}
                rows={rows}
                default={{ orderColumn: -3 }}
              />
            </div>
          </Loader>
        </div>
      </div>
    </>
  );
};

export default PracticeTracking;
